const LABELARY_URL = 'https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/';
/** Labelary allows 3 requests per second per IP. */
const MIN_INTERVAL_MS = 400;
const RATE_LIMIT_WAIT_MS = 1500;
const MAX_RATE_LIMIT_RETRIES = 2;
/** Covers the request and the response body, so a stalled call cannot hold the queue. */
const REQUEST_TIMEOUT_MS = 15000;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Labelary renders FedEx ZPL upside-down vs screen reading. Rotate 180° so
 *  preview, email, upload, and print all share the same upright PNG. */
export const rotateImageBlob180 = async (blob: Blob): Promise<Blob> => {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return blob;
    ctx.translate(canvas.width, canvas.height);
    ctx.rotate(Math.PI);
    ctx.drawImage(bitmap, 0, 0);
    const rotated = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/png');
    });
    return rotated ?? blob;
  } finally {
    bitmap.close();
  }
};

const RATE_LIMITED = Symbol('rate-limited');

/** One Labelary call: the PNG, RATE_LIMITED on a 429, or null when unavailable. Aborts after REQUEST_TIMEOUT_MS. */
const postToLabelary = async (
  zplBase64: string
): Promise<Blob | typeof RATE_LIMITED | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const formData = new FormData();
    formData.append(
      'file',
      new Blob([atob(zplBase64)], { type: 'text/plain' }),
      'label.zpl'
    );
    const res = await fetch(LABELARY_URL, {
      method: 'POST',
      body: formData,
      headers: { Accept: 'image/png' },
      signal: controller.signal,
    });
    if (res.status === 429) return RATE_LIMITED;
    return res.ok ? await res.blob() : null;
  } catch {
    // Preview unavailable: the ZPL download still works.
    return null;
  } finally {
    clearTimeout(timer);
  }
};

const fetchUprightPng = async (
  zplBase64: string,
  retriesLeft = MAX_RATE_LIMIT_RETRIES
): Promise<Blob | null> => {
  const rawBlob = await postToLabelary(zplBase64);
  if (rawBlob === RATE_LIMITED) {
    if (retriesLeft <= 0) return null;
    await sleep(RATE_LIMIT_WAIT_MS);
    return fetchUprightPng(zplBase64, retriesLeft - 1);
  }
  if (!rawBlob) return null;
  try {
    return await rotateImageBlob180(rawBlob);
  } catch {
    // Keep Labelary output if canvas rotate fails.
    return rawBlob;
  }
};

let queue: Promise<unknown> = Promise.resolve();

/** Renders a ZPL label to an upright PNG, or null when Labelary is unavailable.
 *  Requests run one at a time, spaced to stay under Labelary's rate limit. */
export const renderZplToPng = (zplBase64: string): Promise<Blob | null> => {
  const result = queue.then(() => fetchUprightPng(zplBase64));
  queue = result.then(
    () => sleep(MIN_INTERVAL_MS),
    () => sleep(MIN_INTERVAL_MS)
  );
  return result;
};

/** Memoises renders per key while pending or successful; a null or rejected result is evicted so a retry renders again. */
export const renderOnce = (
  cache: Map<string, Promise<Blob | null>>,
  key: string,
  render: () => Promise<Blob | null>
): Promise<Blob | null> => {
  const cached = cache.get(key);
  if (cached) return cached;
  const png = render();
  cache.set(key, png);
  const evict = (): void => {
    if (cache.get(key) === png) cache.delete(key);
  };
  png.then((blob) => (blob ? undefined : evict()), evict);
  return png;
};
